import React, { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { exportToCSV } from "../utils/exportToCSV";
import "../styles/AdminDashboard.css"; 
import DashboardStats from "../components/DashboardStats";

const API_URL = "https://btmw1l4cu9.execute-api.us-east-1.amazonaws.com/prod/leads";
const SUBSCRIBE_API = "https://2fqip35f6h.execute-api.us-east-1.amazonaws.com/prod/create-checkout-session";
const CANCEL_API = "https://q4zf5wficb.execute-api.us-east-1.amazonaws.com/prod/cancel-subscription";

const stripePromise = loadStripe("pk_live_51PUMMK01kX7MCZ6dPDH8ZafylbQTSS8ZUkyeaMNrrvpMaouNRrj5jXrqikbiatbdnVxGWTzVY3CHYDeedJU8v5rt00e1KGv0Hd"); // your public key

const AdminDashboard = ({ isSubscribed, subscriptionId, setIsSubscribed, setSubscriptionId }) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const totalLeads = leads.length;
  const leadsThisMonth = leads.filter(lead => {
    const date = new Date(lead.createdAt);
    const now = new Date();
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  }).length;
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDate, setFilterDate] = useState("");
  
const avgLeadsPerDay = Math.round(totalLeads / 30); // You can adjust if needed
const conversionRate = "35%"; // Hardcoded for now – you can later calculate real one

const domainStats = leads.reduce((acc, lead) => {
  const domain = lead.email.split("@")[1] || "unknown";
  acc[domain] = (acc[domain] || 0) + 1;
  return acc;
}, {});

const hourStats = leads.reduce((acc, lead) => {
  const hour = new Date(lead.createdAt).getHours();
  acc[hour] = (acc[hour] || 0) + 1;
  return acc;

}, {});

const filteredLeads = leads.filter((lead) => {
  const matchesSearch =
    lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email.toLowerCase().includes(searchTerm.toLowerCase());

  const matchesDate =
    !filterDate || new Date(lead.createdAt).toDateString() === new Date(filterDate).toDateString();

  return matchesSearch && matchesDate;
});

  // Sync isSubscribed prop into local state
  useEffect(() => {
    setIsSubscribed(isSubscribed);
  }, [isSubscribed, subscriptionId]);

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const token = auth.user?.id_token;
        if (!token) return;

        const res = await fetch(API_URL, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();
        setLeads(data.leads || []);
      } catch (err) {
        console.error("Failed to fetch leads:", err);
      } finally {
        setLoading(false);
      }
    };

    if (auth.isAuthenticated && auth.user) {
      fetchLeads();
    }
  }, [auth.isAuthenticated, auth.user]);

  const handleSubscribe = async () => {

    setLoading(true);
    try {
      const res = await fetch(SUBSCRIBE_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: auth.user?.profile?.email }),
      });
      const stripe = await stripePromise; // ✅ This line was missing
      const data = await res.json();
      
      if (stripe && data.sessionId) {
        await stripe.redirectToCheckout({ sessionId: data.sessionId });
      } else {
        alert("Failed to create checkout session.");
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
      alert("Checkout error.");
      setLoading(false);
    }
  };
  

  const handleCancel = async () => {
    try {
      if (!subscriptionId) {
        alert("No active subscription to cancel.");
        return;
      }

      const res = await fetch(CANCEL_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ subscriptionId }),
      });

      const data = await res.json();
      console.log(data)
      alert("Subscription canceled!");
      setIsSubscribed(false); // Update the state in App.js
      setSubscriptionId(null); // Update the state in App.js
    } catch (err) {
      console.error(err);
      alert("Failed to cancel subscription.");
    }
  };
  return (
    <div className="dashboard-container">
      <button className="back-btn" onClick={() => navigate("/")}>
        ← Back to Home
      </button>
      <h1>📋 Lead Management Dashboard</h1>
      {/* 📊 Reporting Section */}
      <div className="reporting-section">
        <h2>📈 Reporting Overview</h2>
        <div className="report-grid">
          <div className="report-card">
            <h3>{leads.length}</h3>
            <p>Total Leads</p>
          </div>
          <div className="report-card">
            <h3>
              {
                leads.filter(
                  (lead) => new Date(lead.createdAt).getMonth() === new Date().getMonth()
                ).length
              }
            </h3>
            <p>Leads This Month</p>
          </div>
          <div className="report-card">
            <h3>{Math.round(leads.length / 30)}</h3>
            <p>Avg Leads/Day</p>
          </div>
          <div className="report-card">
            <h3>35%</h3>
            <p>Conversion Rate - mocked</p>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="center">Loading leads...</p>
      ) : leads.length === 0 ? (
        <p className="center empty">No leads yet.</p>
      ) : (
        <>
          {/* ✅ Download CSV Button */}
          <div style={{ textAlign: "center", marginBottom: "1rem" }}>
            <button
              className="subscribe-btn"
              onClick={() => exportToCSV(leads, "leads-export.csv")}
            >
              ⬇️ Download CSV
            </button>
          </div>
          <DashboardStats domainStats={domainStats} hourStats={hourStats} />
          <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem", flexWrap: "wrap" }}>
          <input
            type="text"
            placeholder="Search by name or email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: "0.5rem", flex: "1", minWidth: "200px" }}
          />
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            style={{ padding: "0.5rem", minWidth: "180px" }}
          />
          <button
            className="clear-btn"
            onClick={() => {
              setSearchTerm("");
              setFilterDate("");
            }}
          >
            Clear Filters
          </button>
        </div>

          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Submitted At</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead, i) => (
                  <tr key={i}>
                    <td data-label="Name">{lead.name}</td>
                    <td data-label="Email">{lead.email}</td>
                    <td data-label="Submitted At">
                      {new Date(lead.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}


      <div className="subscribe-section">
        {isSubscribed && subscriptionId ? (
          <>
            <h2>✅ You’re Subscribed!</h2>
            <p>You have full access to Lead Amplify features.</p>
            <button className="cancel-btn" onClick={handleCancel}>
              Cancel Subscription
            </button>
          </>
        ) : (
          <>
            <h2>🔐 Unlock Premium Features</h2>
            <p>Subscribe to access all advanced features of Lead Amplify.</p>
            <button
              className="subscribe-btn"
              onClick={handleSubscribe}
              disabled={loading}
            >
              {loading ? "Redirecting..." : "Subscribe Now"}
            </button>

            
            {/* ✅ INSERT HERE: Loading Spinner/Text */}
            {loading && (
              <p style={{ marginTop: "1rem", color: "#007bff" }}>
                🔄 Redirecting to secure payment page...
              </p>
            )}
          </>
        )}
      </div>
    </div>

  );

  
};

export default AdminDashboard;